import {
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  UnorderedList,
  ListItem,
  AlertDialogFooter,
  Button,
  Text,
  useToast,
  Tooltip,
  IconButton,
  useDisclosure,
} from "@chakra-ui/react";
import { apiRequest } from "utils/api";
import { TeamMember } from "./TeamMemberList";
import { useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export function ReactivateMemberDialog({
  userToReactivate,
  onSuccess,
}: {
  userToReactivate: TeamMember;
  onSuccess: () => void;
}) {
  const cancelRef = useRef(null);
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleReactivate = async (userId: string) => {
    try {
      setIsLoading(true);
      await apiRequest("/supabase/users/reactivate", { id: userId });
      toast({
        title: "User reactivated successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onSuccess();
      onClose();
    } catch (error: any) {
      toast({
        title: "Reactivation Failed",
        description: error.message || "Failed to reactivate user",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Tooltip label="Reactivate user">
        <IconButton
          aria-label="Reactivate user"
          icon={<FontAwesomeIcon icon="user-check" />}
          variant="ghost"
          colorScheme="green"
          size="sm"
          onClick={onOpen}
        />
      </Tooltip>
      <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose} size="xl">
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Deactivate User Access
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to reactivate access for {userToReactivate?.first_name}{" "}
              {userToReactivate?.last_name}?
              <Text mt={6} fontWeight="medium">
                This will:
              </Text>
              <UnorderedList mt={2}>
                <ListItem>Allow the user to log in</ListItem>
                <ListItem>Allow the user to access all features</ListItem>
              </UnorderedList>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose} disabled={isLoading}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={() => userToReactivate && handleReactivate(userToReactivate.id)}
                ml={3}
                isLoading={isLoading}
              >
                Reactivate Access
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
